import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Toolbar,
  Typography,
} from '@mui/material';

import './index.scss';
import agriPixLogo from '../../assets/icons/agripixlogo.svg';
import { GLOBAL_CONSTANTS } from '../../utils/appConstant';
import {
  CUSTOM_EVENTS,
  appLogEvent,
} from '../../utils/analytics';
import { useLazyGetAuthInfoQuery, useAuthenticateWithCodeMutation } from '../../services/auth';
import { AppCircularProgress } from '../../components/AppCircularProgress';
import { showAlert } from '../../reduxActions/alert';
import { useAppDispatch } from '../../storeHook';
import { getDecryptedToken } from '../../utils/functions/authHelpers';

export const OktaSignIn: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const hasRun = useRef(false);
  const [getAuthInfo, { isLoading: isLoadingAuthInfo }] = useLazyGetAuthInfoQuery();
  const [authenticateWithCode, { isLoading: isLoadingAuth }] = useAuthenticateWithCodeMutation();

  const fetchUserTokenAndAuthenticate = async (code: string) => {
    try {
      await authenticateWithCode({ code }).unwrap();

      dispatch(
        showAlert({
          content: 'You are successfully logged in',
          severity: 'success',
        })
      );

      navigate('/');
    } catch (error: unknown) {
      console.error('Authentication error:', error);
      const errorMessage =
        (error as { data?: { detail?: string; error?: string } })?.data?.detail ||
        (error as { data?: { detail?: string; error?: string } })?.data?.error ||
        'Authentication failed';
      dispatch(
        showAlert({
          content: errorMessage,
          severity: 'error',
        })
      );
    }
  };

  const fetchAuthenticatedAPI = async () => {
    try {
      const response = await getAuthInfo().unwrap();
      const loginUrl = response?.login_url;
      if (loginUrl) {
        window.location.href = loginUrl;
      }
    } catch (error) {
      console.error('There was an error!', error);
      dispatch(
        showAlert({
          content: 'Failed to get authentication info',
          severity: 'error',
        })
      );
    }
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = getDecryptedToken();

    // Extract code from window.location.search after Azure redirects back
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');

    if (token) {
      navigate('/');
      return;
    }

    if (code) {
      fetchUserTokenAndAuthenticate(code);
    }
  }, []);

  const callLogin = () => {
    appLogEvent(CUSTOM_EVENTS.SSO_SIGN_IN);
    fetchAuthenticatedAPI();
  };

  const isLoading = isLoadingAuthInfo || isLoadingAuth;

  return (
    <Box className="login-box">
      {isLoading && <AppCircularProgress />}
      <Box className="inner-container">
        <Toolbar className="tool-bar">
          <Typography className="welcome-text">
            {GLOBAL_CONSTANTS.WELCOME_MESSAGE}
          </Typography>
          <Box>
            <img
              src={agriPixLogo}
              alt="Nurture Logo"
              height={'45.25px'}
              width={'159.49px'}></img>
          </Box>
        </Toolbar>

        <Box>
          <Typography className="login-text">
            {GLOBAL_CONSTANTS.LOGIN_MESSAGE}
          </Typography>
        </Box>
        <Button
          className="login"
          variant="contained"
          onClick={callLogin}
          fullWidth
          disabled={isLoading}>
          {GLOBAL_CONSTANTS.LOGIN_BUTTON}
        </Button>
      </Box>
    </Box>
  );
};
